'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useMemo, useRef, useState } from 'react';

import { LanguageSwitcher } from '@/components/language-switcher';
import { useTranslations } from '@/components/language-provider';
import { ThemeToggle } from '@/components/theme-toggle';
import { useTheme } from '@/components/theme-provider';
import { COURT_INTELLIGENCE_FOUNDING_VENUES_PATH } from '@/lib/court-intelligence-marketing';

const CI_ONBOARDING_ENTRY = '/main-app/court-intelligence/onboarding/account';
const PLAYER_LOGIN_HREF = '/auth/login';
const PLAYER_SIGNUP_HREF = '/auth/signup';
const OPERATOR_LOGIN_HREF = `/auth/login?returnTo=${encodeURIComponent(CI_ONBOARDING_ENTRY)}`;
const OPERATOR_SIGNUP_HREF = `/auth/signup?returnTo=${encodeURIComponent(CI_ONBOARDING_ENTRY)}`;

const NAV_ITEMS = [
  { key: 'features', href: '/#features' },
  { key: 'workflow', href: '/#workflow' },
  { key: 'courtIntel', href: '/court-intelligence' },
  { key: 'foundingProgram', href: COURT_INTELLIGENCE_FOUNDING_VENUES_PATH },
  { key: 'plans', href: '/subscription-plans' },
] as const;

type AuthChooserMode = 'login' | 'signup' | null;

export function AppHeader() {
  const { messages } = useTranslations();
  const { actualTheme } = useTheme();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [authChooserMode, setAuthChooserMode] = useState<AuthChooserMode>(null);
  const desktopChooserRef = useRef<HTMLDivElement | null>(null);
  const mobileChooserRef = useRef<HTMLDivElement | null>(null);

  const navItems = useMemo(
    () =>
      NAV_ITEMS.map((item) => ({
        ...item,
        label: messages.nav[item.key],
      })),
    [messages.nav],
  );

  const authChooser = messages.nav.authChooser;
  const chooserTitle = authChooserMode === 'login' ? authChooser.loginTitle : authChooser.signupTitle;
  const chooserDescription =
    authChooserMode === 'login' ? authChooser.loginDescription : authChooser.signupDescription;
  const playerHref = authChooserMode === 'login' ? PLAYER_LOGIN_HREF : PLAYER_SIGNUP_HREF;
  const operatorHref = authChooserMode === 'login' ? OPERATOR_LOGIN_HREF : OPERATOR_SIGNUP_HREF;
  const logoSrc = actualTheme === 'dark' ? '/logo-icon-dark.svg' : '/logo-icon-light.svg';

  useEffect(() => {
    if (!mobileNavOpen && !authChooserMode) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (authChooserMode) {
          setAuthChooserMode(null);
          return;
        }
        setMobileNavOpen(false);
      }
    };
    document.addEventListener('keydown', handler);
    const shouldLockBody =
      mobileNavOpen || (authChooserMode !== null && typeof window !== 'undefined' && window.innerWidth < 768);
    document.body.style.overflow = shouldLockBody ? 'hidden' : '';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [authChooserMode, mobileNavOpen]);

  useEffect(() => {
    if (!authChooserMode) return;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      const clickedDesktopChooser = desktopChooserRef.current?.contains(target) ?? false;
      const clickedMobileChooser = mobileChooserRef.current?.contains(target) ?? false;
      if (!clickedDesktopChooser && !clickedMobileChooser) {
        setAuthChooserMode(null);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [authChooserMode]);

  const closeMobileNav = () => setMobileNavOpen(false);
  const closeAuthChooser = () => setAuthChooserMode(null);
  const openAuthChooser = (mode: Exclude<AuthChooserMode, null>) => {
    closeMobileNav();
    setAuthChooserMode(mode);
  };

  return (
    <header className="sticky top-0 z-50 border-b border-border/70 bg-background/95 backdrop-blur">
      <nav className="relative mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-4 md:px-6">
        <Link href="/" className="flex items-center gap-3 text-lg font-semibold tracking-tight">
          <Image
            src={logoSrc}
            alt="WhoPlays US"
            width={188}
            height={52}
            priority
            className="h-13 w-auto"
          />
          <span className="text-2xl font-semibold tracking-tight">
            <span className="text-foreground">Who</span>
            <span className="ml-1 text-primary">Plays</span>
          </span>
        </Link>
        <div className="hidden items-center gap-6 text-sm font-medium text-muted-foreground md:flex">
          {navItems.map((item) => (
            <Link
              key={item.key}
              href={item.href}
              className="transition hover:text-foreground hover:underline hover:underline-offset-4"
            >
              {item.label}
            </Link>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <div className="hidden items-center gap-2 lg:flex">
            <div className="scale-90 origin-right">
              <LanguageSwitcher />
            </div>
            <div className="scale-90 origin-right">
              <ThemeToggle />
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm font-medium">
            <button
              type="button"
              className="hidden rounded-full px-3 py-1.5 text-muted-foreground transition hover:text-foreground sm:inline-flex"
              onClick={() => openAuthChooser('login')}
            >
              {messages.nav.login}
            </button>
            <button
              type="button"
              className="hidden rounded-full bg-gradient-to-r from-primary to-primary/80 px-4 py-2 text-sm font-semibold btn-primary shadow-md shadow-primary/40 transition hover:-translate-y-0.5 hover:shadow-lg md:inline-flex"
              onClick={() => openAuthChooser('signup')}
            >
              {messages.nav.signup}
            </button>
          </div>
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-full border border-border/70 p-2 text-sm font-semibold text-foreground transition hover:-translate-y-0.5 hover:bg-muted/60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring md:hidden"
            onClick={() => setMobileNavOpen(true)}
            aria-label="Open navigation"
          >
            <span className="sr-only">Open navigation</span>
            <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
              <path
                d="M4 6h16M4 12h16M4 18h16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
      </nav>

      {mobileNavOpen ? (
        <div className="fixed inset-0 z-40 bg-black/60 md:hidden" aria-modal="true" role="dialog">
          <button
            type="button"
            className="absolute inset-0 h-full w-full"
            aria-label="Close navigation"
            onClick={closeMobileNav}
          />
          <div className="ml-auto flex h-full w-64 max-w-[75vw] flex-col gap-6 border-l border-border/60 bg-background p-6 shadow-2xl">
            <div className="flex items-center justify-between">
              <span className="text-base font-semibold text-muted-foreground">Menu</span>
              <button
                type="button"
                className="rounded-full border border-border/70 p-2 text-sm transition hover:bg-muted/60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                onClick={closeMobileNav}
                aria-label="Close navigation"
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
                  <path
                    d="M6 6l12 12M18 6l-12 12"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
            <div className="flex flex-col gap-4 text-sm font-semibold text-foreground bg-background/95">
              {navItems.map((item) => (
                <Link
                  key={item.key}
                  href={item.href}
                  className="rounded-xl border border-border/50 px-4 py-3 transition hover:bg-muted/50"
                  onClick={closeMobileNav}
                >
                  {item.label}
                </Link>
              ))}
            </div>
            <div className="flex flex-col gap-3 bg-background/95">
              <button
                type="button"
                className="rounded-full border border-border/70 px-4 py-2 text-center text-sm font-semibold text-muted-foreground hover:text-foreground"
                onClick={() => openAuthChooser('login')}
              >
                {messages.nav.login}
              </button>
              <button
                type="button"
                className="rounded-full bg-gradient-to-r from-primary to-primary/80 px-4 py-2 text-center text-sm font-semibold text-white shadow-md shadow-primary/40 hover:-translate-y-0.5 hover:shadow-lg"
                onClick={() => openAuthChooser('signup')}
              >
                {messages.nav.signup}
              </button>
            </div>
            <div className="flex items-center justify-between rounded-2xl border border-border/60 px-3 py-2">
              <LanguageSwitcher />
              <ThemeToggle />
            </div>
          </div>
        </div>
      ) : null}

      {authChooserMode ? (
        <>
          <button
            type="button"
            className="fixed inset-0 z-[55] hidden md:block"
            aria-label={authChooser.closeLabel}
            onClick={closeAuthChooser}
          />
          <div className="absolute inset-x-0 top-full z-[60] mt-3 hidden md:block">
            <div className="mx-auto flex max-w-6xl justify-end px-4 md:px-6">
              <div ref={desktopChooserRef} className="w-full max-w-sm rounded-2xl border border-border/70 bg-surface p-4 shadow-2xl">
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-1">
                    <h2 className="text-base font-semibold text-foreground">{chooserTitle}</h2>
                    <p className="text-xs text-muted-foreground">{chooserDescription}</p>
                  </div>
                  <button
                    type="button"
                    onClick={closeAuthChooser}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-border/60 text-lg leading-none text-muted-foreground transition hover:text-foreground"
                    aria-label={authChooser.closeLabel}
                    title={authChooser.closeLabel}
                  >
                    ×
                  </button>
                </div>

                <div className="mt-4 space-y-2">
                  <AuthOptionCard
                    href={playerHref}
                    label={authChooser.playerLabel}
                    description={authChooser.playerDescription}
                    onClick={closeAuthChooser}
                  />
                  <AuthOptionCard
                    href={operatorHref}
                    label={authChooser.operatorLabel}
                    description={authChooser.operatorDescription}
                    onClick={closeAuthChooser}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="fixed inset-0 z-[60] flex items-start justify-center p-4 pt-20 md:hidden" role="dialog" aria-modal="true">
            <button
              type="button"
              className="absolute inset-0 bg-black/55 backdrop-blur-[1px]"
              aria-label={authChooser.closeLabel}
              onClick={closeAuthChooser}
            />
            <div
              ref={mobileChooserRef}
              className="relative w-full max-w-sm rounded-2xl border border-border/70 bg-surface p-4 shadow-2xl"
            >
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <h2 className="text-base font-semibold text-foreground">{chooserTitle}</h2>
                <p className="text-xs text-muted-foreground">{chooserDescription}</p>
              </div>
              <button
                type="button"
                onClick={closeAuthChooser}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-border/60 text-lg leading-none text-muted-foreground transition hover:text-foreground"
                aria-label={authChooser.closeLabel}
                title={authChooser.closeLabel}
              >
                ×
              </button>
            </div>

            <div className="mt-4 space-y-2">
              <AuthOptionCard
                href={playerHref}
                label={authChooser.playerLabel}
                description={authChooser.playerDescription}
                onClick={closeAuthChooser}
              />
              <AuthOptionCard
                href={operatorHref}
                label={authChooser.operatorLabel}
                description={authChooser.operatorDescription}
                onClick={closeAuthChooser}
              />
            </div>
            </div>
          </div>
        </>
      ) : null}
    </header>
  );
}

function AuthOptionCard({
  href,
  label,
  description,
  onClick,
}: {
  href: string;
  label: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <Link
      href={href}
      onClick={onClick}
      className="group flex items-center justify-between gap-4 rounded-2xl border border-border/60 bg-background px-4 py-3 shadow-sm transition hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-lg"
    >
      <div className="space-y-1">
        <p className="text-sm font-semibold text-foreground">{label}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <span className="text-sm font-semibold text-primary transition group-hover:text-primary/80" aria-hidden="true">
        →
      </span>
    </Link>
  );
}
